import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { toast } from "sonner";
import {
  LOST_REASONS, LOST_STATUSES, LEAD_STAGES as STAGE_TEMPS, APPOINTMENT_STATUS,
  APPT_STATUS_ROLES, appointmentStatusOptionsFor,
  formatDate, formatDateTime, statusLabel, isDispositionStage,
} from "@/lib/constants";
import { StatusChangeDialog, RescheduleDialog, VisitedDialog } from "@/pages/Appointments";
import { useMasterData } from "@/hooks/use-master-data";
import { StageBadge, StatusBadge, RepeatBadge } from "@/components/crm/Badges";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Phone, MessageCircle, CalendarPlus, StickyNote,
  PhoneCall, CalendarClock, CalendarCheck, Pencil, Paperclip, Download, Trash2, Upload, Check,
  Mic, Play, Square, Mail, Bell, Activity, ChevronLeft, ChevronRight, Clock, X, Info,
  ArrowUpRight, ArrowDownLeft, PhoneMissed, UserCheck,
  IndianRupee, Wallet, Stethoscope, MapPin, CircleAlert, Eye, EyeOff, Loader2, Ban,
} from "lucide-react";
import {
  ANY_DOCTOR, BOOK_BRANCH_KEY, todayISO, computeSlots, bookableBranches, defaultBranch,
} from "@/lib/bookingSlots";
import WhatsAppChat from "@/components/crm/WhatsAppChat";
import {
  BlacklistBanner, BlacklistLeadDialog, BlockedWhen, canBlacklistLead, canUnblacklistLead,
} from "@/components/crm/LeadBlacklist";
import { LEAD_BLACKLIST } from "@/constants/testIds";
import { smartDueLabel, smartDueUrgency, FOLLOWUP_PRESETS } from "@/lib/constants";
import ChannelSelector from "@/components/crm/followups/ChannelSelector";
import DueDatePresets from "@/components/crm/followups/DueDatePresets";
import OutcomeChipGrid from "@/components/crm/followups/OutcomeChipGrid";
import AttemptDots from "@/components/crm/followups/AttemptDots";
import { maskPhone, maskPhoneList } from "@/lib/phone";

/* ── Active progression shown on the visual stepper (excludes dead-end statuses) ── */
const PIPELINE_STAGES = [
  "new_lead", "call_back", "appointment_booked", "interested", "visited",
];

/* ── Short labels for the stepper dots ── */
const STEPPER_LABELS = {
  new_lead: "New",
  call_back: "Call Back",
  appointment_booked: "Booked",
  interested: "Interested",
  visited: "Visited",
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // hq_admin has full parity with super_admin (backend ADMIN_BYPASS_ROLES).
  const isSuperAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  // Managers/admins may change a lead's branch (an ownership field); others see it read-only.
  const canEditBranch = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"].includes(user?.role);
  const md = useMasterData();
  const { stages: LEAD_STAGES, sources: SOURCES, statuses: LEAD_STATUSES, subStatuses: LEAD_SUB_STATUSES, categories: LEAD_CATEGORIES, concerns: LEAD_CONCERNS, services: SERVICES, subSources, locations: LOCATIONS } = md;
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [calls, setCalls] = useState([]);
  const [callInfo, setCallInfo] = useState(null); // call selected for the Info dialog
  const [history, setHistory] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [newStage, setNewStage] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [followOpen, setFollowOpen] = useState(false);
  const [completeFu, setCompleteFu] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  // Inline appointment status change from the Appointments tab. Same flow as the
  // Appointments page: plain changes confirm with an optional note; "rescheduled"
  // needs a new slot; "visited" captures outcome + revenue/outstanding.
  const canChangeApptStatus = APPT_STATUS_ROLES.includes(user?.role);
  const [apptStatusChange, setApptStatusChange] = useState(null); // {apptId, newStatus, apptName}
  const [apptRescheduling, setApptRescheduling] = useState(null); // appointment being rescheduled
  const [apptVisiting, setApptVisiting] = useState(null);         // appointment being marked visited
  const [branches, setBranches] = useState([]);
  // Phone reveal: numbers are masked everywhere; "View" fetches the full numbers
  // via the audited POST /leads/:id/phone-view and keeps them for this page visit
  // only (never on load, never persisted). `phoneShown` toggles View/Hide locally.
  const [phoneReveal, setPhoneReveal] = useState(null); // {mobile, alt_mobile, whatsapp, viewed_at}
  const [phoneShown, setPhoneShown] = useState(false);
  const [phoneRevealing, setPhoneRevealing] = useState(false);
  useEffect(() => { setPhoneReveal(null); setPhoneShown(false); }, [id]);

  const togglePhoneReveal = async () => {
    // Hide drops the numbers; every View is a fresh audited call (same as the app).
    if (phoneShown) { setPhoneShown(false); setPhoneReveal(null); return; }
    setPhoneRevealing(true);
    try {
      const { data } = await api.post(`/leads/${id}/phone-view`, { source: "web" });
      setPhoneReveal(data || {});
      setPhoneShown(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhoneRevealing(false); }
  };
  const revealed = phoneShown ? phoneReveal : null;

  const load = useCallback(() => {
    api.get(`/leads/${id}`).then((r) => {
      const d = r.data;
      if (d.status) d.status = d.status.toLowerCase();
      if (d.stage) d.stage = d.stage.toLowerCase();
      if (d.sub_status) d.sub_status = d.sub_status.toLowerCase();
      setLead(d);
      setNewStage(d.status);
    }).catch(() => navigate("/leads"));
    api.get(`/leads/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
    api.get(`/leads/${id}/calls`).then((r) => setCalls(r.data)).catch(() => {});
    api.get(`/leads/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
    api.get(`/leads/${id}/followups`).then((r) => setFollowups(r.data)).catch(() => {});
    api.get(`/appointments`, { params: { lead_id: id } }).then((r) => setAppointments(r.data)).catch(() => {});
    api.get(`/documents`, { params: { lead_id: id } }).then((r) => setDocuments(r.data)).catch(() => {});
    api.get(`/users`).then((r) => setUsers(r.data)).catch(() => {});
    api.get(`/branches`).then((r) => setBranches(r.data)).catch(() => {});
  }, [id, navigate]);

  // Plain appointment status change (non-visited, non-reschedule) from the
  // Appointments tab, confirmed via StatusChangeDialog with an optional note.
  const confirmApptStatusChange = async (note) => {
    if (!apptStatusChange) return;
    try {
      await api.put(`/appointments/${apptStatusChange.apptId}`, {
        status: apptStatusChange.newStatus,
        status_note: note || "",
      });
      toast.success("Appointment status updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setApptStatusChange(null);
  };

  useEffect(() => { load(); }, [load]);

  // Real-time: reload this lead when an event for it (or its follow-ups) arrives.
  useLiveEvents((event) => {
    if (event.lead_id === id || event.type === "followup.changed") {
      load();
    }
  });

  const save = useCallback(async (field, value) => {
    try {
      const { data: updated } = await api.put(`/leads/${id}`, { [field]: value === "" ? null : value });
      setLead((prev) => ({ ...prev, ...updated }));
      // Keep a revealed number in sync after editing it.
      if (["mobile", "alt_mobile", "whatsapp"].includes(field)) {
        setPhoneReveal((prev) => (prev ? { ...prev, [field]: updated?.[field] ?? value } : prev));
      }
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);

  const [stageChangeOpen, setStageChangeOpen] = useState(false);
  const [stageNote, setStageNote] = useState("");
  const [stageTempChangeOpen, setStageTempChangeOpen] = useState(false);
  const [pendingStageTemp, setPendingStageTemp] = useState("");
  const [stageTempNote, setStageTempNote] = useState("");

  const confirmStatusChange = async () => {
    try {
      await api.post(`/leads/${id}/stage`, { stage: newStage, lost_reason: lostReason || null, note: stageNote });
      toast.success("Status updated");
      setStageNote("");
      setStageChangeOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmStageTempChange = async () => {
    try {
      await api.put(`/leads/${id}`, { stage: pendingStageTemp });
      await api.post(`/leads/${id}/notes`, { text: `Stage changed to ${pendingStageTemp}${stageTempNote ? ": " + stageTempNote : ""}` });
      toast.success("Stage updated");
      setStageTempNote("");
      setStageTempChangeOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/leads/${id}/notes`, { text: noteText });
    setNoteText("");
    setMentionOpen(false);
    api.get(`/leads/${id}/notes`).then((r) => setNotes(r.data));
    toast.success("Note added");
  };

  const uploadDocument = async (file, docType = "other") => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    formData.append("lead_id", id);
    try {
      await api.post("/documents", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      api.get(`/documents`, { params: { lead_id: id } }).then((r) => setDocuments(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteDocument = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Document deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadDocument = async (doc) => {
    const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", doc.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // ── Call recording inline player ──
  const [playerCallId, setPlayerCallId] = useState(null);
  const [playerUrl, setPlayerUrl] = useState(null);
  const [loadingRecId, setLoadingRecId] = useState(null);

  const togglePlayer = async (call) => {
    if (playerCallId === call.id) {
      if (playerUrl) window.URL.revokeObjectURL(playerUrl);
      setPlayerCallId(null);
      setPlayerUrl(null);
      return;
    }
    setLoadingRecId(call.id);
    try {
      const res = await api.get(`/leads/${id}/calls/${call.id}/recording`, { responseType: "blob" });
      if (playerUrl) window.URL.revokeObjectURL(playerUrl);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      setPlayerUrl(url);
      setPlayerCallId(call.id);
    } catch (e) { toast.error("Could not load recording"); }
    finally { setLoadingRecId(null); }
  };

  const downloadRecording = async (call) => {
    try {
      const res = await api.get(`/leads/${id}/calls/${call.id}/recording`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", call.recording_name || "recording.mp3");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download recording"); }
  };

  // ── @mention autocomplete ──
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const noteInputRef = useRef(null);

  const handleNoteChange = (e) => {
    const val = e.target.value;
    setNoteText(val);
    const caret = e.target.selectionStart;
    const upToCaret = val.slice(0, caret);
    const match = upToCaret.match(/@([\w' .-]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const selectMention = (name) => {
    const caret = noteInputRef.current?.selectionStart ?? noteText.length;
    const upToCaret = noteText.slice(0, caret);
    const after = noteText.slice(caret);
    const replaced = upToCaret.replace(/@([\w' .-]*)$/, `@${name} `);
    const newVal = replaced + after;
    setNoteText(newVal);
    setMentionOpen(false);
    setTimeout(() => noteInputRef.current?.focus(), 0);
  };

  const filteredMentionUsers = users.filter((u) =>
    u.name.toLowerCase().includes(mentionQuery.toLowerCase())
  ).slice(0, 6);

  const convertToPatient = async () => {
    try {
      await api.post("/patients", { name: lead.name, mobile: lead.mobile, email: lead.email,
        gender: lead.gender, dob: lead.dob || null, age: lead.age, city: lead.city, branch_id: lead.branch_id, lead_id: lead.id });
      toast.success("Converted to patient");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Go back to the leads list preserving its filters. Prefer history-back so the
  // previous /leads?<filters> URL is restored; fall back to a bare /leads only
  // when there's no in-app history (e.g. the lead was opened via a direct link).
  const goBackToLeads = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/leads");
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete lead "${lead.name}" (${maskPhone(lead.mobile)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!lead) return <div className="py-12 text-center text-muted-foreground">Loading…</div>;

  const isLost = ["lost", "junk"].includes(newStage);
  // A lead in a dead-end status has its follow-ups auto-closed by the backend —
  // hide every follow-up creation/scheduling affordance for it.
  const leadStatusLost = LOST_STATUSES.includes((lead.status || "").toLowerCase());
  // Blacklisted: the server 403s calls, follow-ups, bookings, phone reveals and
  // status/owner changes, so those controls are off (with a tooltip) up front.
  const bl = !!lead.blacklisted;

  return (
    <div className="space-y-6">
      <button onClick={goBackToLeads} className="flex items-center gap-2 text-sm font-medium text-navy hover:underline" data-testid="back-to-leads">
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </button>

      <BlacklistBanner lead={lead} canRemove={canUnblacklistLead(user)} onChanged={load} />

      {PIPELINE_STAGES.includes(lead.status) && (
        <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
          <StageStepper
            stage={lead.status}
            history={history}
            createdAt={lead.created_at}
            rottingDays={LEAD_STATUSES.find((s) => s.value.toLowerCase() === lead.status)?.rotting_days}
          />
        </div>
      )}

      {/* min-w-0 on both columns: grid items default to min-width:auto, so the
          one-line tab bar used to widen the right column past the viewport and
          scroll the whole page sideways. */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left panel */}
        <div className="min-w-0 space-y-4">

          {/* Header card */}
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs text-muted-foreground">{lead.friendly_id || "—"}</p>
                <h2 className="mt-0.5 font-display text-2xl font-semibold text-navy" data-testid="lead-detail-name">{lead.name}</h2>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" />
                  <span data-testid="lead-detail-mobile">{revealed ? (revealed.mobile ?? lead.mobile) : maskPhone(lead.mobile)}</span>
                  {(lead.mobile || lead.whatsapp || lead.alt_mobile) && (
                    <BlockedWhen blocked={bl && !phoneShown}>
                    <button
                      type="button"
                      onClick={togglePhoneReveal}
                      disabled={phoneRevealing || (bl && !phoneShown)}
                      className="ml-1 inline-flex items-center gap-1 rounded-md border border-navy/15 px-1.5 py-0.5 text-xs font-medium text-navy hover:bg-navy/5 disabled:opacity-50"
                      data-testid="phone-view-btn"
                    >
                      {phoneRevealing ? <Loader2 className="h-3 w-3 animate-spin" /> : phoneShown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {phoneRevealing ? "Loading…" : phoneShown ? "Hide" : "View"}
                    </button>
                    </BlockedWhen>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {canBlacklistLead(user) && !bl && (
                  <Button variant="outline" size="sm" onClick={() => setBlacklistOpen(true)}
                    className="h-8 border-red-200 px-2.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={LEAD_BLACKLIST.openButton}>
                    <Ban className="mr-1 h-3.5 w-3.5" /> Blacklist
                  </Button>
                )}
                {isSuperAdmin && (
                  <button onClick={handleDelete} className="rounded-lg p-2 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete lead" data-testid="delete-lead-btn">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusBadge status={lead.status} color={LEAD_STATUSES.find((s) => s.value.toLowerCase() === lead.status)?.color} />
              <StageBadge stage={lead.stage} color={LEAD_STAGES.find((s) => s.value.toLowerCase() === lead.stage)?.color} />
              <RepeatBadge count={lead.repeat_count} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {bl ? (
                <>
                  <BlockedWhen blocked className="flex">
                    <button disabled className="flex w-full cursor-not-allowed flex-col items-center gap-1 rounded-lg bg-nude/60 py-2.5 text-xs font-medium text-navy opacity-50" data-testid="action-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
                  </BlockedWhen>
                  <BlockedWhen blocked className="flex">
                    <button disabled className="flex w-full cursor-not-allowed flex-col items-center gap-1 rounded-lg bg-nude/60 py-2.5 text-xs font-medium text-navy opacity-50" data-testid="action-log-call"><PhoneCall className="h-4 w-4" /> Log Call</button>
                  </BlockedWhen>
                </>
              ) : (
                <>
                  <a href={`https://wa.me/${(lead.whatsapp || lead.mobile || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-lg bg-nude/60 py-2.5 text-xs font-medium text-navy hover:bg-nude" data-testid="action-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                  <button onClick={() => setCallOpen(true)} className="flex flex-col items-center gap-1 rounded-lg bg-nude/60 py-2.5 text-xs font-medium text-navy hover:bg-nude" data-testid="action-log-call"><PhoneCall className="h-4 w-4" /> Log Call</button>
                </>
              )}
            </div>
          </div>

          {/* Pipeline controls */}
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="mb-3 font-display font-semibold text-navy">Update Pipeline</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                {lead.status === "appointment_booked" ? (
                  <div>
                    <div className="flex h-10 items-center rounded-md border border-amber-300 bg-amber-50 px-3 text-sm font-medium text-amber-800">
                      Appointment Booked (locked until visited)
                    </div>
                    <Button size="sm" variant="outline" disabled={bl} title={bl ? "Lead is blacklisted" : undefined} className="mt-2 w-full border-green-300 text-green-700 hover:bg-green-50" onClick={() => { setNewStage("visited"); setStageChangeOpen(true); }}>
                      Mark as Visited
                    </Button>
                  </div>
                ) : (
                  <Select value={newStage} onValueChange={(v) => { setNewStage(v); setStageChangeOpen(true); }} disabled={bl}>
                    <SelectTrigger data-testid="stage-select" title={bl ? "Lead is blacklisted" : undefined}><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Stage">
                <Select value={lead.stage} onValueChange={(v) => { setPendingStageTemp(v); setStageTempChangeOpen(true); }} disabled={bl}>
                  <SelectTrigger data-testid="stage-temp-select" title={bl ? "Lead is blacklisted" : undefined}><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGE_TEMPS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-4 flex gap-2">
              {!leadStatusLost && (
                <BlockedWhen blocked={bl} className="flex flex-1">
                  <Button variant="outline" onClick={() => setFollowOpen(true)} disabled={bl} className="flex-1 border-navy/20 text-navy hover:bg-navy/5" data-testid="schedule-followup-btn">
                    <CalendarPlus className="mr-1.5 h-4 w-4" /> Follow-up
                  </Button>
                </BlockedWhen>
              )}
              {!lead.converted && (
                <Button onClick={convertToPatient} className="flex-1 bg-gold text-white hover:bg-gold-hover" data-testid="convert-patient-btn">Convert</Button>
              )}
            </div>
          </div>

          <LeadRevenueCard appointments={appointments} />

        </div>

        {/* Right: activity tabs */}
        <div className="min-w-0 lg:col-span-2">
          <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
            <Tabs defaultValue="details">
              {/* Scrolls inside the card when the tabs don't fit, instead of
                  overflowing it. The bar is always shown so the hidden tabs are
                  discoverable: styling ::-webkit-scrollbar opts Chrome/Safari out
                  of macOS's auto-hiding overlay bars. Don't add the standard
                  scrollbar-width/scrollbar-color here — Chrome ignores the
                  ::-webkit-scrollbar rules whenever those are set. */}
              <TabsList className="flex h-auto w-full justify-start overflow-x-auto bg-cream pb-2 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-navy/30 [&::-webkit-scrollbar-thumb:hover]:bg-navy/50 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-navy/5 [&>button]:shrink-0">
                <TabsTrigger value="details" data-testid="tab-details"><Info className="mr-1.5 h-4 w-4" /> Details</TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="mr-1.5 h-4 w-4" /> Notes</TabsTrigger>
                <TabsTrigger value="calls" data-testid="tab-calls"><PhoneCall className="mr-1.5 h-4 w-4" /> Calls</TabsTrigger>
                <TabsTrigger value="followups" data-testid="tab-followups">
                  <CalendarClock className="mr-1.5 h-4 w-4" /> Follow-ups
                  {followups.length > 0 && <span className="ml-1.5 rounded-full bg-navy/10 px-1.5 text-xs font-semibold text-navy">{followups.length}</span>}
                </TabsTrigger>
                <TabsTrigger value="appointments" data-testid="tab-appointments">
                  <CalendarCheck className="mr-1.5 h-4 w-4" /> Appointments
                  {appointments.length > 0 && <span className="ml-1.5 rounded-full bg-navy/10 px-1.5 text-xs font-semibold text-navy">{appointments.length}</span>}
                </TabsTrigger>
                <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history"><Activity className="mr-1.5 h-4 w-4" /> Activity</TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">
                  <Paperclip className="mr-1.5 h-4 w-4" /> Documents
                  {documents.length > 0 && <span className="ml-1.5 rounded-full bg-navy/10 px-1.5 text-xs font-semibold text-navy">{documents.length}</span>}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4">
                <div className="space-y-4">

                  <DetailSection
                    title="Followup Details"
                    lead={lead}
                    onSave={save}
                    fields={[
                      { label: "First Contacted On", field: "first_contacted_on", type: "date", readOnly: true },
                      { label: "Last Contacted On",  field: "last_contacted_on",  type: "date", readOnly: true },
                      { label: "Next Followup On",   field: "next_follow_up",     type: "date", readOnly: true },
                    ]}
                  />

                  <DetailSection
                    title="Customer Information"
                    lead={lead}
                    onSave={save}
                    phoneReveal={revealed}
                    fields={[
                      { label: "Name",                 field: "name" },
                      { label: "Mobile",               field: "mobile", phone: true },
                      { label: "WhatsApp Number",      field: "whatsapp", phone: true },
                      { label: "Alternate Phone Number", field: "alt_mobile", phone: true },
                      { label: "Email",                field: "email" },
                      { label: "Gender",               field: "gender", type: "select", options: ["Male", "Female", "Other"] },
                      { label: "Age",                  field: "age",    type: "number", render: (v) => (v == null || v === "") ? <span className="text-slate-300">-</span> : String(v) },
                      // After Age so a DOB saved in the same edit wins — the server derives age from it.
                      { label: "Date of Birth",        field: "dob",    type: "date" },
                      { label: "City",                 field: "city" },
                      { label: "Location",             field: "location", type: "select", options: LOCATIONS },
                      { label: "State",                field: "state" },
                      { label: "Country",              field: "country" },
                      { label: "Language",             field: "language" },
                      { label: "Category",             field: "category", type: "select", options: LEAD_CATEGORIES },
                      { label: "Appointment Date",     field: "appointment_date", type: "datetime", readOnly: true },
                      { label: "Last Interaction",     field: "last_interaction", type: "datetime", readOnly: true },
                    ]}
                  />

                  <DetailSection
                    title="Pipeline"
                    lead={lead}
                    onSave={save}
                    fields={[
                      { label: "Source",      field: "source",     type: "select", options: SOURCES },
                      { label: "Sub Source",  field: "sub_source", type: "select", options: subSources.filter((s) => s.parent === lead.source) },
                      lead.status === "appointment_booked"
                        ? { label: "Status", field: "status", readOnly: true, render: () => "Appointment Booked (locked until visited)" }
                        : { label: "Status", field: "status", type: "select", options: LEAD_STATUSES },
                      { label: "Sub Status",  field: "sub_status", type: "select", options: LEAD_SUB_STATUSES },
                      { label: "Concern",     field: "concern",    type: "select", options: LEAD_CONCERNS },
                      { label: "Service",     field: "interested_service", type: "select", options: SERVICES },
                      { label: "Would you like to visit us", field: "wants_visit" },
                      { label: "Remark",      field: "remark",     type: "textarea", full: true },
                    ]}
                  />

                  <DetailSection
                    title="Other Information"
                    lead={lead}
                    onSave={save}
                    fields={[
                      { label: "Deal Value",   field: "deal_value", type: "number" },
                      { label: "Revenue",      field: "revenue",    type: "number" },
                      { label: "UTM Source",   field: "utm_source" },
                      { label: "UTM Medium",   field: "utm_medium" },
                      { label: "UTM Campaign", field: "utm_campaign" },
                      { label: "Created At",      field: "created_at",  type: "datetime", readOnly: true },
                      { label: "Lead Assigned On", field: "assigned_at", type: "date", readOnly: true },
                      { label: "Creator",   field: "created_by_name", readOnly: true, render: (v, l) => v || l.created_by || <span className="text-slate-300">-</span> },
                      { label: "Owner",     field: "assigned_to_name", readOnly: true, render: (v) => v || "Unassigned" },
                      { label: "Franchise", field: "franchise_name", readOnly: true },
                      canEditBranch
                        ? { label: "Branch", field: "branch_id", type: "select",
                            options: (user?.role === "branch_manager" && (user?.branch_ids || []).length
                              ? branches.filter((b) => (user.branch_ids || []).includes(b.id))
                              : branches).map((b) => ({ value: b.id, label: b.name })),
                            render: (v) => v ? (branches.find((b) => b.id === v)?.name || v) : <span className="text-slate-300">-</span> }
                        : { label: "Branch", field: "branch_name", readOnly: true },
                    ]}
                  />
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-4">
                <div className="relative flex gap-2">
                  <Input
                    ref={noteInputRef}
                    value={noteText}
                    onChange={handleNoteChange}
                    placeholder="Add a note… use @Name to mention a teammate"
                    onKeyDown={(e) => { if (e.key === "Enter" && !mentionOpen) addNote(); if (e.key === "Escape") setMentionOpen(false); }}
                    data-testid="note-input"
                  />
                  <Button onClick={addNote} className="bg-navy text-white hover:bg-navy-hover" data-testid="add-note-btn">Add</Button>
                  {mentionOpen && filteredMentionUsers.length > 0 && (
                    <div className="absolute left-0 top-full z-10 mt-1 w-64 rounded-lg border border-navy/10 bg-white shadow-lg">
                      {filteredMentionUsers.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => selectMention(u.name)}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-cream/60"
                        >
                          <span className="font-medium text-ink">{u.name}</span>
                          <span className="ml-1.5 text-xs text-muted-foreground capitalize">{u.role?.replace("_", " ")}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-4 space-y-3">
                  {notes.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No notes yet</p> :
                    notes.map((n) => (
                      <div key={n.id} className="rounded-lg bg-cream/60 p-3">
                        <p className="text-sm text-ink">{renderNoteText(n.text)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{n.user_name} · {formatDateTime(n.created_at)}</p>
                      </div>
                    ))}
                </div>
              </TabsContent>

              <TabsContent value="calls" className="mt-4 space-y-3">
                <div className="flex justify-end">
                  <BlockedWhen blocked={bl}>
                    <Button onClick={() => setCallOpen(true)} disabled={bl} className="bg-navy text-white hover:bg-navy-hover" data-testid="tab-log-call">
                      <PhoneCall className="mr-1.5 h-4 w-4" /> Log Call
                    </Button>
                  </BlockedWhen>
                </div>
                {calls.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No calls logged</p> :
                  calls.map((c) => (
                    <CallCard
                      key={c.id}
                      call={c}
                      onInfo={() => setCallInfo(c)}
                      onPlay={() => togglePlayer(c)}
                      onDownload={() => downloadRecording(c)}
                      playing={playerCallId === c.id}
                      loadingRec={loadingRecId === c.id}
                      playerUrl={playerCallId === c.id ? playerUrl : null}
                      onEnded={() => { setPlayerCallId(null); window.URL.revokeObjectURL(playerUrl); setPlayerUrl(null); }}
                    />
                  ))}
              </TabsContent>

              <TabsContent value="followups" className="mt-4">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm font-medium text-navy">Patient Nurture Timeline</p>
                  {!leadStatusLost && (
                    <BlockedWhen blocked={bl}>
                      <Button size="sm" variant="outline" onClick={() => setFollowOpen(true)} disabled={bl} className="border-navy/20 text-navy hover:bg-navy/5" data-testid="followup-tab-schedule-btn">
                        <CalendarPlus className="mr-1.5 h-4 w-4" /> Schedule
                      </Button>
                    </BlockedWhen>
                  )}
                </div>
                {followups.length > 0 && <NurtureSummaryCard followups={followups} />}
                <div className="relative mt-4">
                  {followups.length === 0 ? (
                    <div className="py-12 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-nude">
                        <Activity className="h-6 w-6 text-navy/40" />
                      </div>
                      <p className="font-medium text-navy">No follow-ups yet</p>
                      <p className="mt-1 text-xs text-muted-foreground">{leadStatusLost ? "This lead is closed — no follow-up needed" : "Schedule a follow-up to start nurturing this lead"}</p>
                    </div>
                  ) : (
                    <div className="relative pl-6">
                      <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-navy/10" />
                      {followups.map((f, idx) => (
                        <TimelineNode
                          key={f.id}
                          f={f}
                          md={md}
                          isFirst={idx === 0}
                          isLast={idx === followups.length - 1}
                          onComplete={() => setCompleteFu(f)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="appointments" className="mt-4">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Appointments for this lead</p>
                  <BlockedWhen blocked={bl}>
                    <Button size="sm" variant="outline" onClick={() => setApptOpen(true)} disabled={bl} className="border-navy/20 text-navy hover:bg-navy/5" data-testid="appt-tab-book-btn">
                      <CalendarCheck className="mr-1.5 h-4 w-4" /> Book
                    </Button>
                  </BlockedWhen>
                </div>
                {appointments.length > 0 && <LeadApptSummary appointments={appointments} />}
                <div className="space-y-3">
                  {appointments.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No appointments booked</p> :
                    appointments.map((a) => {
                      const st = APPOINTMENT_STATUS.find((s) => s.value === a.status);
                      const badge = (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${st?.color || "bg-gray-100 text-gray-700"}`}>
                          {st?.label || a.status}
                        </span>
                      );
                      return (
                        <LeadApptCard
                          key={a.id}
                          appt={a}
                          doctor={users.find((u) => u.id === a.doctor_id)}
                          branch={branches.find((b) => b.id === a.branch_id)}
                          statusControl={canChangeApptStatus ? (
                            <Select value={a.status} onValueChange={(v) => {
                              if (v === a.status) return;
                              // Rescheduling needs a new slot; Visited captures
                              // outcome + revenue — same routing as Appointments.
                              if (v === "rescheduled") { setApptRescheduling(a); return; }
                              if (v === "visited") { setApptVisiting(a); return; }
                              setApptStatusChange({ apptId: a.id, newStatus: v, apptName: a.patient_name });
                            }}>
                              <SelectTrigger className="h-auto w-auto gap-1 border-none bg-transparent p-0 shadow-none" data-testid={`lead-appt-status-${a.id}`}>
                                {badge}
                              </SelectTrigger>
                              <SelectContent>{appointmentStatusOptionsFor(user?.role, a.status).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : badge}
                        />
                      );
                    })}
                </div>
              </TabsContent>

              <TabsContent value="whatsapp" className="mt-4">
                <WhatsAppChat leadId={id} lead={lead} blocked={bl} />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <ActivityTimeline history={history} calls={calls} notes={notes} appointments={appointments} users={users} />
              </TabsContent>

              <TabsContent value="documents" className="mt-4">
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-navy/20 bg-cream/40 py-6 text-sm font-medium text-navy hover:bg-cream/60" data-testid="upload-document-label">
                  <Upload className="h-4 w-4" /> Upload prescription, ID, or photo
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={(e) => { uploadDocument(e.target.files[0]); e.target.value = ""; }}
                    data-testid="upload-document-input"
                  />
                </label>
                <div className="mt-4 space-y-2">
                  {documents.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No documents uploaded</p> :
                    documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between rounded-lg bg-cream/60 p-3" data-testid="document-item">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                            <p className="text-xs text-muted-foreground">{d.uploaded_by_name} · {formatDateTime(d.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => downloadDocument(d)} data-testid="download-document-btn"><Download className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteDocument(d.id)} data-testid="delete-document-btn"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </div>
                    ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <FollowUpDialog open={followOpen} onOpenChange={setFollowOpen} lead={lead} md={md} onDone={load} />
      <LeadCompleteFollowUpDialog followUp={completeFu} md={md} onClose={() => setCompleteFu(null)} onDone={load} />
      <CallLogDialog open={callOpen} onOpenChange={setCallOpen} leadId={id} lead={lead} md={md} branches={branches} onDone={load} />
      <CallInfoDialog call={callInfo} onClose={() => setCallInfo(null)} />
      <BookAppointmentDialog open={apptOpen} onOpenChange={setApptOpen} lead={lead} doctors={users.filter((u) => u.role === "doctor")} branches={branches} md={md} onDone={load} />
      <BlacklistLeadDialog lead={lead} open={blacklistOpen} onClose={() => setBlacklistOpen(false)} onDone={load} />

      {/* Appointment status flows shared with the Appointments page. */}
      <StatusChangeDialog data={apptStatusChange} onClose={() => setApptStatusChange(null)} onConfirm={confirmApptStatusChange} />
      <RescheduleDialog appt={apptRescheduling} onClose={() => setApptRescheduling(null)} onDone={load} />
      <VisitedDialog appt={apptVisiting} onClose={() => setApptVisiting(null)} onDone={load} />

      {/* Status change confirmation */}
      <Dialog open={stageChangeOpen} onOpenChange={(open) => { if (!open) { setStageChangeOpen(false); setNewStage(lead.status); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-navy">Update Status</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Changing status from <span className="font-semibold text-ink">{statusLabel(lead.status)}</span> to <span className="font-semibold text-ink">{statusLabel(newStage)}</span>
            </p>
            {isLost && (
              <Field label="Lost Reason">
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>{LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Note (optional)">
              <Textarea value={stageNote} onChange={(e) => setStageNote(e.target.value)} placeholder="Why is the status being changed?" rows={2} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStageChangeOpen(false); setNewStage(lead.status); }}>Cancel</Button>
            <Button onClick={confirmStatusChange} className="bg-navy text-white hover:bg-navy-hover" data-testid="confirm-status-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage (temperature) change confirmation */}
      <Dialog open={stageTempChangeOpen} onOpenChange={(open) => { if (!open) setStageTempChangeOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-navy">Update Stage</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Changing stage from <span className="font-semibold text-ink">{lead.stage}</span> to <span className="font-semibold text-ink">{pendingStageTemp}</span>
            </p>
            <Field label="Note (optional)">
              <Textarea value={stageTempNote} onChange={(e) => setStageTempNote(e.target.value)} placeholder="Why is the stage being changed?" rows={2} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageTempChangeOpen(false)}>Cancel</Button>
            <Button onClick={confirmStageTempChange} className="bg-navy text-white hover:bg-navy-hover">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Unified activity timeline ──────────────────────────────────────────────
   Merges every touchpoint on a lead — calls, notes, appointments, and the
   lead_history events (created / status change / follow-up / WhatsApp / repeat)
   — into one reverse-chronological feed. Calls, notes and appointments live in
   their own collections, so they're folded in here on the client. */
/* ── Appointments tab ───────────────────────────────────────────────────── */

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const hasMoney = (v) => v != null && v !== "";
// Still expected to happen — counts as "upcoming" once its slot is in the future.
const OPEN_APPT_STATUSES = ["scheduled", "confirmed", "ringing", "rescheduled", "checked_in", "in_consultation", "checked_out"];

// Money is credited on VISITED appointments only, as collected + outstanding —
// the same rule as /reports/my-performance and the dashboard Revenue tile, so the
// numbers here reconcile with those.
function leadApptStats(appointments) {
  const now = Date.now();
  let visited = 0, noShow = 0, cancelled = 0, upcoming = 0, converted = 0, collected = 0, due = 0;
  let lastVisit = null;
  for (const a of appointments) {
    if (a.status === "visited") {
      visited += 1;
      if (a.visit_outcome !== "not_converted") converted += 1;
      collected += Number(a.revenue) || 0;
      due += Number(a.outstanding) || 0;
      if (!lastVisit || a.scheduled_at > lastVisit) lastVisit = a.scheduled_at;
    } else if (a.status === "no_show") noShow += 1;
    else if (a.status === "cancelled") cancelled += 1;
    else if (OPEN_APPT_STATUSES.includes(a.status) && new Date(a.scheduled_at).getTime() >= now) upcoming += 1;
  }
  const reached = visited + noShow;
  return {
    visited, noShow, cancelled, upcoming, converted, collected, due, lastVisit,
    billed: collected + due,
    showRate: reached ? Math.round((visited / reached) * 100) : null,
  };
}

function LeadApptSummary({ appointments }) {
  const { visited, noShow, cancelled, upcoming, converted, collected, due, billed, showRate } = leadApptStats(appointments);

  const tiles = [
    {
      label: "Appointments", value: appointments.length, icon: CalendarClock,
      sub: [upcoming && `${upcoming} upcoming`, cancelled && `${cancelled} cancelled`].filter(Boolean).join(" · ") || "all time",
    },
    {
      label: "Visited", value: visited, icon: UserCheck,
      sub: showRate == null ? "none due yet" : `${showRate}% show rate${noShow ? ` · ${noShow} no-show` : ""}`,
    },
    {
      label: "Collected", value: inr(collected), icon: IndianRupee, tone: "gold",
      sub: billed ? `of ${inr(billed)} billed` : "nothing billed yet",
    },
    {
      label: "Outstanding", value: inr(due), icon: Wallet, tone: due > 0 ? "red" : "muted",
      sub: due > 0 ? "yet to be paid" : visited ? "fully paid" : "—",
    },
  ];
  const toneCls = {
    gold: "bg-gold/15 text-gold",
    red: "bg-red-50 text-red-600",
    muted: "bg-navy/5 text-navy/60",
  };
  const valueCls = { gold: "text-gold", red: "text-red-600" };

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="lead-appt-summary">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-xl border border-navy/10 bg-white p-3">
          <div className="flex items-center gap-2">
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${toneCls[t.tone] || "bg-navy/10 text-navy"}`}>
              <t.icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-xs font-medium text-muted-foreground">{t.label}</span>
          </div>
          <p className={`mt-2 font-display text-xl font-semibold ${valueCls[t.tone] || "text-navy"}`}>{t.value}</p>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{t.sub}</p>
          {t.label === "Visited" && visited > 0 && (
            <p className="text-[11px] text-muted-foreground">{converted} converted</p>
          )}
        </div>
      ))}
    </div>
  );
}

// Left-panel money snapshot for the lead — same figures as the Appointments tab
// summary (visited appointments only), so the two never disagree.
function LeadRevenueCard({ appointments }) {
  const { visited, collected, due, billed, lastVisit, upcoming } = leadApptStats(appointments);
  const pct = billed ? Math.round((collected / billed) * 100) : 0;

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm" data-testid="lead-revenue-card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display font-semibold text-navy">Revenue</h3>
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gold/15 text-gold">
          <IndianRupee className="h-3.5 w-3.5" />
        </span>
      </div>

      {billed === 0 ? (
        <p className="rounded-lg bg-cream/60 px-3 py-4 text-center text-xs text-muted-foreground">
          {visited ? "Visited, but no payment recorded yet" : "No revenue yet — shows once an appointment is marked Visited"}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Collected</p>
          <p className="font-display text-3xl font-semibold text-gold" data-testid="lead-revenue-collected">{inr(collected)}</p>

          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-navy/10">
            <div className="h-full rounded-full bg-gold" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">{pct}% of {inr(billed)} billed</p>

          <div className="mt-4">
            <div className={`rounded-lg px-3 py-2 ${due > 0 ? "bg-red-50" : "bg-cream/60"}`}>
              <p className="text-[11px] text-muted-foreground">Outstanding</p>
              <p className={`text-sm font-semibold ${due > 0 ? "text-red-600" : "text-navy"}`} data-testid="lead-revenue-due">{inr(due)}</p>
            </div>
          </div>
        </>
      )}

      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span>{visited} visit{visited === 1 ? "" : "s"}</span>
        {lastVisit && <span>· last {formatDate(lastVisit)}</span>}
        {upcoming > 0 && <span>· {upcoming} upcoming</span>}
      </div>
    </div>
  );
}

function LeadApptCard({ appt: a, doctor, branch, statusControl }) {
  const d = new Date(a.scheduled_at);
  const valid = !isNaN(d.getTime());
  const day = valid ? d.toLocaleDateString("en-IN", { day: "2-digit" }) : "—";
  const month = valid ? d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase() : "";
  const weekday = valid ? d.toLocaleDateString("en-IN", { weekday: "short" }) : "";
  const time = valid ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
  const year = valid && d.getFullYear() !== new Date().getFullYear() ? d.getFullYear() : null;

  const visited = a.status === "visited";
  const paid = Number(a.revenue) || 0;
  const due = Number(a.outstanding) || 0;
  const anyMoney = hasMoney(a.revenue) || due > 0;
  const muted = ["cancelled", "no_show"].includes(a.status);

  return (
    <div
      className={`overflow-hidden rounded-xl border border-navy/10 bg-white transition-shadow hover:shadow-sm ${muted ? "opacity-75" : ""}`}
      data-testid="appointment-item"
    >
      <div className="flex gap-3 p-3">
        {/* Date chip */}
        <div className={`flex w-14 shrink-0 flex-col items-center justify-center rounded-lg py-1.5 ${visited ? "bg-green-50 text-green-800" : "bg-cream text-navy"}`}>
          <span className="text-[10px] font-semibold tracking-wider opacity-70">{month}</span>
          <span className="font-display text-xl font-bold leading-none">{day}</span>
          <span className="mt-0.5 text-[10px] opacity-70">{year || weekday}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{a.service || "General consultation"}</p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> {time}
                {a.duration_minutes ? <span>· {a.duration_minutes} min</span> : null}
              </p>
            </div>
            <div className="shrink-0">{statusControl}</div>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {doctor && <span className="inline-flex items-center gap-1"><Stethoscope className="h-3 w-3" /> {doctor.name}</span>}
            {branch && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {branch.name}</span>}
            {a.booked_by_name && <span className="inline-flex items-center gap-1"><UserCheck className="h-3 w-3" /> Booked by {a.booked_by_name}</span>}
          </div>
          {a.notes && <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{a.notes}</p>}
        </div>
      </div>

      {/* Money / outcome footer */}
      {(visited || anyMoney) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-navy/5 bg-cream/40 px-3 py-2 text-xs" data-testid={`lead-appt-revenue-${a.id}`}>
          {visited && !anyMoney ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <CircleAlert className="h-3.5 w-3.5" /> No payment recorded
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-0.5 font-semibold text-green-800">
                <IndianRupee className="h-3 w-3" /> {visited ? "Paid" : "Expected"} {inr(paid)}
              </span>
              {due > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 font-semibold text-red-700">
                  <Wallet className="h-3 w-3" /> Due {inr(due)}
                </span>
              ) : visited ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-navy/5 px-2 py-0.5 font-medium text-navy/70">
                  <Check className="h-3 w-3" /> No dues
                </span>
              ) : null}
              {due > 0 && <span className="text-muted-foreground">Total {inr(paid + due)}</span>}
            </>
          )}
          {visited && a.visit_outcome && (
            <span className={`ml-auto rounded-full px-2 py-0.5 font-medium ${a.visit_outcome === "not_converted" ? "bg-amber-50 text-amber-800" : "bg-navy/10 text-navy"}`}>
              {a.visit_outcome === "not_converted"
                ? `Not converted${a.not_converted_reason ? ` · ${a.not_converted_reason}` : ""}`
                : "Converted"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityTimeline({ history = [], calls = [], notes = [], appointments = [], users = [] }) {
  const docName = (id) => users.find((u) => u.id === id)?.name;

  const items = [];

  // lead_history events
  for (const h of history) {
    let label, tone = "gold";
    if (h.type === "stage_change") label = <>Status → <span className="font-semibold">{statusLabel(h.to_stage)}</span></>;
    else if (h.type === "repeat_enquiry") { label = <span className="font-semibold text-gold">Repeat enquiry</span>; tone = "gold-ring"; }
    else if (h.type === "whatsapp_received") { label = <span><MessageCircle className="inline h-3 w-3 mr-1 text-green-500" />WhatsApp received</span>; tone = "green"; }
    else if (h.type === "whatsapp_sent") { label = <span><MessageCircle className="inline h-3 w-3 mr-1 text-green-600" />WhatsApp sent</span>; tone = "green"; }
    else if (h.type === "followup_scheduled") label = <span><CalendarPlus className="inline h-3 w-3 mr-1 text-navy/60" />Follow-up scheduled</span>;
    else if (h.type === "followup_completed") label = <span><CalendarCheck className="inline h-3 w-3 mr-1 text-navy/60" />Follow-up completed</span>;
    else if (h.type === "assigned") label = <span><UserCheck className="inline h-3 w-3 mr-1 text-navy/60" />Assigned</span>;
    else if (h.type === "created" || !h.type) label = "Lead created";
    else label = <span className="capitalize">{String(h.type).replace(/_/g, " ")}</span>;
    items.push({ id: `h-${h.id}`, at: h.created_at, tone, user: h.user_name, label, note: h.note });
  }

  // calls
  for (const c of calls) {
    items.push({
      id: `c-${c.id}`, at: c.created_at, tone: "navy", user: c.user_name,
      label: <span><PhoneCall className="inline h-3 w-3 mr-1 text-navy/60" />Call logged{c.disposition ? ` — ${c.disposition}` : ""}</span>,
      note: c.notes,
    });
  }

  // notes
  for (const n of notes) {
    items.push({
      id: `n-${n.id}`, at: n.created_at, tone: "navy", user: n.user_name,
      label: <span><StickyNote className="inline h-3 w-3 mr-1 text-navy/60" />Note added</span>,
      note: n.text,
    });
  }

  // appointments
  for (const a of appointments) {
    const st = APPOINTMENT_STATUS.find((s) => s.value === a.status);
    const dr = docName(a.doctor_id);
    items.push({
      id: `a-${a.id}`, at: a.created_at || a.scheduled_at, tone: "gold", user: a.created_by_name,
      label: <span><CalendarClock className="inline h-3 w-3 mr-1 text-navy/60" />Appointment {st?.label || a.status} · {formatDateTime(a.scheduled_at)}{dr ? ` · ${dr}` : ""}</span>,
    });
  }

  items.sort((x, y) => String(y.at || "").localeCompare(String(x.at || "")));

  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No activity yet</p>;
  }

  const dot = (tone) =>
    tone === "gold-ring" ? "bg-gold ring-2 ring-gold/30"
    : tone === "green" ? "bg-green-500"
    : tone === "navy" ? "bg-navy"
    : "bg-gold";

  return (
    <div className="relative space-y-4 pl-5">
      {items.map((it) => (
        <div key={it.id} className="relative">
          <span className={`absolute -left-5 top-1.5 h-2.5 w-2.5 rounded-full ${dot(it.tone)}`} />
          <p className="text-sm text-ink">
            {it.label}
            {it.note && <span className="text-muted-foreground"> · {it.note}</span>}
          </p>
          <p className="text-xs text-muted-foreground">{[it.user, formatDateTime(it.at)].filter(Boolean).join(" · ")}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Highlight @mentions in note text ───────────────────────────────────── */
function renderNoteText(text) {
  const parts = text.split(/(@[\w' .-]+?(?=\s|$))/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? <span key={i} className="font-semibold text-navy">{part}</span> : part
  );
}

/* ── Visual stage stepper with "rotting in days" badge ──────────────────── */
function StageStepper({ stage, history, createdAt, rottingDays }) {
  if (stage === "lost" || stage === "junk") return null;

  const currentIdx = PIPELINE_STAGES.indexOf(stage);
  if (currentIdx === -1) return null;

  // Find the most recent time the lead entered its current stage
  const lastChange = (history || []).find((h) => h.type === "stage_change" && h.to_stage === stage);
  const since = lastChange?.created_at || createdAt;
  const days = since ? Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 86400000)) : null;
  const rotThreshold = rottingDays > 0 ? rottingDays : 7;
  const warnThreshold = Math.max(1, Math.floor(rotThreshold / 2));

  return (
    <div>
      <div className="flex items-start">
        {PIPELINE_STAGES.map((s, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          return (
            <React.Fragment key={s}>
              {i > 0 && (
                <div className={`mt-2.5 h-0.5 flex-1 ${i <= currentIdx ? "bg-navy" : "bg-navy/10"}`} />
              )}
              <div className="relative flex w-12 shrink-0 flex-col items-center" data-testid={`stepper-stage-${s}`}>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold
                    ${done ? "bg-navy text-white" : current ? "bg-gold text-white ring-2 ring-gold/30" : "bg-navy/10 text-navy/40"}`}
                >
                  {done ? <Check className="h-3 w-3" /> : ""}
                </div>
                <span
                  className={`mt-1 block text-center text-[9px] leading-tight ${current ? "font-semibold text-gold" : "text-navy/40"}`}
                >
                  {STEPPER_LABELS[s] || statusLabel(s)}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {days != null && (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          In <span className="font-semibold text-navy">{statusLabel(stage)}</span> for{" "}
          <span className={`font-semibold ${days >= rotThreshold ? "text-red-600" : days >= warnThreshold ? "text-amber-600" : "text-navy"}`}>
            {days} {days === 1 ? "day" : "days"}
          </span>
        </p>
      )}
    </div>
  );
}

// Normalize a select-option array (strings or {value,label}) to {value,label}[]
function normOpts(options = []) {
  return Array.isArray(options) && options.length && typeof options[0] === "object"
    ? options : (options || []).map((o) => ({ value: o, label: o }));
}

// Display a single field's value (read mode) inside the grid
function fieldDisplay(f, lead, phoneReveal = null) {
  const raw = f.phone && phoneReveal ? (phoneReveal[f.field] ?? lead[f.field]) : lead[f.field];
  if (f.render) return f.render(raw, lead);
  if (raw == null || raw === "" || (Array.isArray(raw) && raw.length === 0)) return <span className="text-slate-300">-</span>;
  if (f.phone) return phoneReveal ? (Array.isArray(raw) ? raw.join(", ") : String(raw)) : maskPhoneList(raw);
  if (f.type === "number") return `₹${Number(raw).toLocaleString("en-IN")}`;
  if (f.type === "date") return formatDate(raw);
  if (f.type === "datetime") return formatDateTime(raw);
  if (f.type === "select") {
    const o = normOpts(f.options).find((x) => x.value === raw);
    return (o && o.label) || raw;
  }
  return String(raw);
}

/**
 * A reference-style section card: titled header with a pencil that toggles the
 * whole section into edit mode. Fields render in a responsive grid
 * (label on top, value below). On save, each changed field is persisted via onSave.
 */
function DetailSection({ title, fields, lead, onSave, readOnly = false, phoneReveal = null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Phone fields (f.phone) are masked and only become editable once the
  // numbers have been revealed via "View" — a masked value is never saved.
  const phoneLocked = (f) => f.phone && !phoneReveal;
  const baseValue = (f) => (f.phone && phoneReveal ? (phoneReveal[f.field] ?? lead[f.field]) : lead[f.field]);
  const editableAll = fields.filter((f) => !f.readOnly);
  const editable = editableAll.filter((f) => !phoneLocked(f));
  const canEdit = !readOnly && editableAll.length > 0;

  const startEdit = () => {
    const d = {};
    editable.forEach((f) => { d[f.field] = baseValue(f) ?? ""; });
    setDraft(d);
    setEditing(true);
  };

  // Revealing mid-edit: seed the now-unlocked phone fields into the draft.
  useEffect(() => {
    if (!editing || !phoneReveal) return;
    setDraft((p) => {
      const d = { ...p };
      fields.forEach((f) => { if (f.phone && !f.readOnly && !(f.field in d)) d[f.field] = phoneReveal[f.field] ?? lead[f.field] ?? ""; });
      return d;
    });
  }, [editing, phoneReveal]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const f of editable) {
        const next = draft[f.field];
        if (String(next ?? "") !== String(baseValue(f) ?? "")) {
          await onSave(f.field, next);
        }
      }
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h4 className="text-sm font-semibold text-slate-600">{title}</h4>
        {canEdit && (
          editing ? (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} disabled={saving}
                className="text-xs font-medium text-slate-400 hover:text-slate-600">Cancel</button>
              <button onClick={saveAll} disabled={saving}
                className="inline-flex items-center gap-1 rounded-md bg-navy px-2.5 py-1 text-xs font-medium text-white hover:bg-navy/90 disabled:opacity-50">
                <Check className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          ) : (
            <button onClick={startEdit} className="text-slate-400 hover:text-navy" aria-label={`Edit ${title}`}>
              <Pencil className="h-4 w-4" />
            </button>
          )
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5 px-5 py-4 sm:grid-cols-3 lg:grid-cols-4">
        {fields.map((f) => (
          <div key={f.field} className={f.full ? "col-span-2 sm:col-span-3 lg:col-span-4" : ""}>
            <p className="mb-1 text-xs text-slate-400">{f.label}</p>
            {editing && !f.readOnly && !phoneLocked(f) ? (
              f.type === "select" ? (
                <Select value={String(draft[f.field] ?? "")} onValueChange={(v) => setDraft((p) => ({ ...p, [f.field]: v }))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{normOpts(f.options).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <textarea
                  value={draft[f.field] ?? ""}
                  onChange={(e) => setDraft((p) => ({ ...p, [f.field]: e.target.value }))}
                  rows={2}
                  className="w-full rounded border border-navy/30 bg-white px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-navy/40 resize-none"
                />
              ) : (
                <input
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  max={f.type === "date" ? new Date().toISOString().slice(0, 10) : undefined}
                  value={draft[f.field] ?? ""}
                  onChange={(e) => setDraft((p) => ({ ...p, [f.field]: e.target.value }))}
                  className="w-full rounded border border-navy/30 bg-white px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-navy/40"
                />
              )
            ) : (
              <>
                <p className="text-sm font-medium text-slate-800">{fieldDisplay(f, lead, phoneReveal)}</p>
                {editing && phoneLocked(f) && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Click View (top left) to edit</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// "visited" / "Visited" / "appointment_visited" / "Appointment Visited" — the
// same normalisation as isDispositionStage (case, spaces/underscores ignored).
const isVisitedValue = (v) =>
  ["visited", "appointment visited"].includes(String(v || "").toLowerCase().replace(/[_\s]+/g, " ").trim());

const _fuLabel = (opts, val) => (opts || []).find((o) => o.value === val)?.label || val;
const _fuColor = (opts, val) => (opts || []).find((o) => o.value === val)?.color || "bg-gray-100 text-gray-700";

const CHANNEL_ICON_MAP = { call: Phone, whatsapp: MessageCircle, email: Mail, in_app: Bell };

function NurtureSummaryCard({ followups }) {
  const total = followups.length;
  const pending = followups.filter((f) => f.status === "pending" || f.status === "overdue").length;
  const doneItems = followups.filter((f) => f.status === "done");
  const connected = doneItems.filter((f) => f.outcome === "connected" || f.outcome === "converted").length;
  const avgAttempts = doneItems.length > 0 ? (doneItems.reduce((s, f) => s + (f.attempt_no || 1), 0) / doneItems.length).toFixed(1) : "—";
  const lastDone = doneItems[0];

  return (
    <div className="rounded-xl border border-navy/10 bg-cream/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-navy/60" />
        <p className="text-xs font-semibold uppercase tracking-wider text-navy/60">Nurture Summary</p>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center">
          <p className="text-lg font-bold font-display text-navy">{total}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-display text-amber-600">{pending}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-display text-emerald-600">{connected}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Connected</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold font-display text-navy">{avgAttempts}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Attempts</p>
        </div>
      </div>
      {lastDone && (
        <p className="mt-3 text-xs text-muted-foreground border-t border-navy/5 pt-2">
          Last outcome: <span className="font-medium text-ink">{statusLabel(lastDone.outcome)}</span> · {formatDateTime(lastDone.due_at)}
        </p>
      )}
    </div>
  );
}

function TimelineNode({ f, md, isFirst, isLast, onComplete }) {
  const done = f.status === "done";
  const overdue = f.status === "overdue";
  const dotColor = done ? "bg-navy" : overdue ? "bg-red-500" : "bg-gold ring-2 ring-gold/30";
  const urgency = smartDueUrgency(f.due_at);
  const dueColor = urgency === "overdue" ? "text-red-600" : urgency === "today" ? "text-amber-600" : "text-emerald-600";
  const ChannelIcon = CHANNEL_ICON_MAP[f.channel] || CalendarClock;

  return (
    <div className="relative pb-4 last:pb-0" data-testid="followup-item">
      <div className={`absolute -left-6 top-3 h-3 w-3 rounded-full ${dotColor}`} />
      {!isLast && done && <div className="absolute -left-[18.5px] top-6 bottom-0 w-0.5 bg-navy/15" />}
      {!isLast && !done && <div className="absolute -left-[18.5px] top-6 bottom-0 w-0.5 border-l border-dashed border-navy/15" />}
      <div className={`rounded-xl border ${overdue ? "border-red-200 bg-red-50/30" : "border-navy/10 bg-white"} p-3 shadow-sm`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${done ? "bg-emerald-100 text-emerald-600" : overdue ? "bg-red-100 text-red-600" : "bg-navy/10 text-navy"}`}>
                <ChannelIcon className="h-3.5 w-3.5" />
              </div>
              <p className={`text-sm font-medium ${done ? "text-muted-foreground line-through" : "text-ink"}`}>{f.title}</p>
              <AttemptDots count={f.attempt_no} />
            </div>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {f.followup_type && <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${_fuColor(md.followupTypes, f.followup_type)}`}>{_fuLabel(md.followupTypes, f.followup_type)}</span>}
              {f.priority && f.priority !== "normal" && <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${_fuColor(md.followupPriorities, f.priority)}`}>{_fuLabel(md.followupPriorities, f.priority)}</span>}
              {done && f.outcome && <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${_fuColor(md.followupOutcomes, f.outcome)}`}>{_fuLabel(md.followupOutcomes, f.outcome)}</span>}
              {overdue && <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">OVERDUE</span>}
            </div>
            <p className={`mt-1.5 text-xs font-medium ${dueColor}`}>{smartDueLabel(f.due_at)}</p>
            {f.notes && <p className="mt-1 text-xs text-muted-foreground">{f.notes}</p>}
            {f.outcome_note && <p className="mt-1 text-xs text-ink"><span className="font-medium">Outcome:</span> {f.outcome_note}</p>}
          </div>
          {!done && (
            <Button size="sm" variant="outline" onClick={onComplete} className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`complete-followup-${f.id}`}>
              <Check className="mr-1 h-3.5 w-3.5" /> Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FollowUpDialog({ open, onOpenChange, lead, md, onDone }) {
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [channel, setChannel] = useState("call");
  const [followupType, setFollowupType] = useState("call_back");
  const [priority, setPriority] = useState("normal");

  useEffect(() => {
    if (open) {
      setTitle(""); setDueAt(""); setChannel("call");
      setFollowupType("call_back"); setPriority("normal");
    }
  }, [open]);

  const applyPreset = (preset) => {
    setTitle(preset.title);
    setFollowupType(preset.type);
    setChannel(preset.channel);
    setPriority(preset.priority);
    const d = new Date(Date.now() + preset.offsetHours * 3600000);
    setDueAt(d.toISOString().slice(0, 16));
  };

  const submit = async () => {
    if (!dueAt) { toast.error("Pick a date/time"); return; }
    try {
      await api.post("/followups", {
        lead_id: lead.id,
        title: title || `Follow up with ${lead.name}`,
        due_at: new Date(dueAt).toISOString(),
        channel, followup_type: followupType, priority,
      });
      toast.success("Follow-up scheduled");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Schedule Follow-up</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex flex-wrap gap-1.5">
            {FOLLOWUP_PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => applyPreset(p)}
                className="rounded-full border border-navy/10 bg-cream/60 px-3 py-1 text-[11px] font-medium text-navy hover:bg-cream transition-colors">
                {p.label}
              </button>
            ))}
          </div>
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Follow up with ${lead.name}`} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={followupType} onValueChange={setFollowupType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(md.followupTypes || []).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(md.followupPriorities || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Channel"><ChannelSelector value={channel} onChange={setChannel} /></Field>
          <Field label="Date & Time">
            <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="followup-datetime" />
            <div className="mt-2"><DueDatePresets onSelect={setDueAt} /></div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-followup-btn">Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadCompleteFollowUpDialog({ followUp, md, onClose, onDone }) {
  const open = !!followUp;
  const [outcome, setOutcome] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [scheduleNext, setScheduleNext] = useState(false);
  const [nextDue, setNextDue] = useState("");
  const [nextType, setNextType] = useState("call_back");
  const [nextPriority, setNextPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (followUp) {
      setOutcome(""); setOutcomeNote(""); setScheduleNext(false); setNextDue("");
      setNextType(followUp.followup_type || "call_back");
      setNextPriority(followUp.priority || "normal");
    }
  }, [followUp]);

  const submit = async () => {
    if (!outcome) { toast.error("Pick an outcome"); return; }
    if (scheduleNext && !nextDue) { toast.error("Pick the next due date"); return; }
    setSaving(true);
    try {
      await api.post(`/followups/${followUp.id}/complete`, {
        outcome, outcome_note: outcomeNote,
        next_due_at: scheduleNext ? new Date(nextDue).toISOString() : null,
        next_type: scheduleNext ? nextType : null,
        next_priority: scheduleNext ? nextPriority : null,
      });
      toast.success(scheduleNext ? "Completed — next follow-up scheduled" : "Follow-up completed");
      onClose(); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Complete Follow-up</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground truncate flex-1">{followUp?.title}</p>
            {(followUp?.attempt_no || 1) > 1 && (
              <span className="shrink-0 text-xs font-medium text-navy">Attempt #{followUp.attempt_no}</span>
            )}
          </div>
          {(followUp?.attempt_no || 1) > 1 && <AttemptDots count={followUp.attempt_no} />}
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-ink">What happened?</p>
            <OutcomeChipGrid outcomes={md.followupOutcomes || []} value={outcome} onChange={setOutcome} />
          </div>
          <Field label="Outcome note">
            <Textarea value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} rows={2} placeholder="e.g. Patient asked to call after 6pm" />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <Checkbox checked={scheduleNext} onCheckedChange={(v) => setScheduleNext(!!v)} />
            <span className="text-sm font-medium text-ink">Schedule next follow-up</span>
          </label>
          {scheduleNext && (
            <div className="space-y-3 rounded-lg border border-navy/10 bg-cream/40 p-3">
              {(followUp?.attempt_no || 1) >= 1 && (
                <p className="text-xs text-muted-foreground">This will be attempt #{(followUp?.attempt_no || 1) + 1} in the nurture chain</p>
              )}
              <Field label="Next due date & time *">
                <Input type="datetime-local" value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
                <div className="mt-2"><DueDatePresets onSelect={setNextDue} /></div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <Select value={nextType} onValueChange={setNextType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(md.followupTypes || []).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={nextPriority} onValueChange={setNextPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(md.followupPriorities || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-700">{saving ? "Saving…" : "Complete"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Icon for a call's direction, matching the reference (↗ outgoing, ↙ incoming).
function CallTypeIcon({ type }) {
  const t = (type || "Outgoing").toLowerCase();
  if (t === "incoming") return <ArrowDownLeft className="h-4 w-4 text-emerald-600" />;
  if (t === "missed") return <PhoneMissed className="h-4 w-4 text-red-500" />;
  return <ArrowUpRight className="h-4 w-4 text-emerald-600" />;
}

// A labeled key/value block used across the call card + info dialog.
function KV({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-sm text-ink">{children || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

// LeadPixie-style call row: header (icon + owner, duration, created, chevron),
// call notes, then a status / follow-up footer + recording controls.
function CallCard({ call: c, onInfo, onPlay, onDownload, playing, loadingRec, playerUrl, onEnded }) {
  return (
    <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
      {/* Header */}
      <button onClick={onInfo} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-cream/40">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
          <CallTypeIcon type={c.call_type} />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-navy">{c.user_name || "—"}</p>
        <div className="hidden shrink-0 text-right sm:block">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Duration</p>
          <p className="text-sm text-ink">{c.duration || 0}s</p>
        </div>
        <div className="hidden shrink-0 text-right sm:block">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Created</p>
          <p className="text-sm text-ink">{formatDateTime(c.created_at)}</p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-navy/40" />
      </button>

      {/* Call notes */}
      {c.notes && (
        <div className="border-t border-navy/5 px-4 py-3">
          <KV label="Call Notes">{c.notes}</KV>
        </div>
      )}

      {/* Footer: status + follow-up + recording controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-navy/5 bg-cream/30 px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-6">
          <KV label="Status">{c.status_at_call ? statusLabel(c.status_at_call) : (c.disposition || null)}</KV>
          <KV label="Follow-up On">{c.follow_up_at_call ? formatDateTime(c.follow_up_at_call) : null}</KV>
          {c.appointment_at_call && <KV label="Appointment On">{formatDateTime(c.appointment_at_call)}</KV>}
        </div>
        {c.has_recording && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 border-navy/15 px-2 text-xs text-navy" onClick={onPlay}>
              {loadingRec
                ? <span className="mr-1 h-3 w-3 animate-spin rounded-full border-2 border-navy border-t-transparent" />
                : playing ? <Square className="mr-1 h-3 w-3 text-red-500" /> : <Play className="mr-1 h-3 w-3" />}
              Recording
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onDownload} title="Download recording">
              <Download className="h-3.5 w-3.5 text-navy" />
            </Button>
          </div>
        )}
      </div>

      {playing && playerUrl && (
        <audio controls autoPlay src={playerUrl} onEnded={onEnded} className="h-9 w-full border-t border-navy/5" />
      )}
    </div>
  );
}

// The "Info" dialog opened by the chevron — full detail for a single call.
function CallInfoDialog({ call: c, onClose }) {
  return (
    <Dialog open={!!c} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-navy">
            <Info className="h-4 w-4" /> Call Info
          </DialogTitle>
        </DialogHeader>
        {c && (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-4">
              <KV label="Caller">{c.user_name}</KV>
              <KV label="Status">{c.status_at_call ? statusLabel(c.status_at_call) : (c.disposition || null)}</KV>
              <KV label="Next Follow-up On">{c.follow_up_at_call ? formatDateTime(c.follow_up_at_call) : null}</KV>
              {c.appointment_at_call && <KV label="Appointment On">{formatDateTime(c.appointment_at_call)}</KV>}
              <KV label="Call Type">{c.call_type || "Outgoing"}</KV>
            </div>
            <KV label="Call Notes">{c.notes}</KV>
            <div className="grid grid-cols-2 gap-4">
              <KV label="Duration">{(c.duration || 0) + "s"}</KV>
              <KV label="Created">{formatDateTime(c.created_at)}</KV>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} className="bg-navy text-white hover:bg-navy-hover">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CallLogDialog({ open, onOpenChange, leadId, lead, md, branches = [], onDone }) {
  const { user } = useAuth();
  const CALL_TYPES = ["Outgoing", "Incoming", "Missed"];

  const [callType, setCallType] = useState("Outgoing");
  const [newStatus, setNewStatus] = useState("");
  const [newStage, setNewStage] = useState("");
  const [newBranchId, setNewBranchId] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newConcern, setNewConcern] = useState("");
  const [notes, setNotes] = useState("");
  const [recordingFile, setRecordingFile] = useState(null);
  const [fuDueAt, setFuDueAt] = useState("");      // next follow-up datetime (empty = none)
  const [apptDate, setApptDate] = useState("");    // appointment date (when status = booked)
  const [saving, setSaving] = useState(false);
  // Live appointment this lead already has. When present, the form manages it
  // (confirm / reschedule / cancel) instead of booking a new one.
  const [existingAppt, setExistingAppt] = useState(null);
  const [apptStatus, setApptStatus] = useState("");   // chosen appointment status in manage mode

  useEffect(() => {
    if (open && lead) {
      setCallType("Outgoing");
      setNewStatus(lead.status || "");
      setNewStage(lead.stage || "");
      // Branch now lives with the appointment-booking part only. Default it the
      // same way BookAppointmentDialog does: the lead's branch, or the caller's
      // single branch when they have exactly one.
      setNewBranchId(lead.branch_id || (user?.branch_ids?.length === 1 ? user.branch_ids[0] : ""));
      setNewLocation(lead.location || "");
      setNewConcern(lead.concern || "");
      setNotes("");
      setRecordingFile(null);
      setFuDueAt("");
      setApptDate(lead.appointment_date ? toLocalDatetimeValue(new Date(lead.appointment_date)) : "");
      setExistingAppt(null);
      setApptStatus("");
      // Find the lead's live appointment so a follow-up call can manage it.
      api.get("/appointments", { params: { lead_id: lead.id } }).then((r) => {
        const terminal = ["cancelled", "visited", "no_show", "completed"];
        const live = (r.data || [])
          .filter((a) => !terminal.includes(a.status))
          .sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || ""));
        if (live[0]) {
          setExistingAppt(live[0]);
          setApptStatus(live[0].status);
          setApptDate(toLocalDatetimeValue(new Date(live[0].scheduled_at)));
        }
      }).catch(() => {});
    }
  }, [open, lead]);

  const hasLiveAppointment = !!existingAppt;
  const isRescheduling = hasLiveAppointment && apptStatus === "rescheduled";
  const MANAGE_APPT_STATUSES = ["scheduled", "confirmed", "ringing", "rescheduled", "cancelled", "no_show"];

  const { statuses: LEAD_STATUSES = [], stages: LEAD_STAGES = [], concerns: LEAD_CONCERNS = [], locations: LEAD_LOCATIONS = [] } = md || {};

  // Follow-up is mandatory for active hot/warm leads — but NOT when the caller
  // marks a dead-end status (not interested, wrong/invalid number, etc.), which
  // closes the lead.
  // Booking an appointment reveals a required Appointment Date field, and makes
  // the follow-up OPTIONAL — the appointment itself is the next touchpoint, so we
  // don't force a separate follow-up on top of it.
  const isAppointmentBooked = newStatus === "appointment_booked";

  // Dead-end status (not interested, wrong/invalid number, location) closes the
  // lead: the backend rejects POST /followups for it, so hide the follow-up
  // fields entirely and never send a follow-up.
  const statusIsLost = LOST_STATUSES.includes(newStatus);

  // A visit already happened (status "visited", or a "Visited" / "Appointment
  // Visited" stage) — the follow-up stays available but is never forced.
  const isVisited = isVisitedValue(newStatus) || isVisitedValue(newStage);

  const followUpRequired = ["hot", "warm"].includes((newStage || "").toLowerCase())
    && !statusIsLost
    && !isAppointmentBooked
    && !isVisited;

  // Switching to a lost status clears any pre-picked follow-up so the payload
  // stays clean (no POST /followups, no follow_up_at_call).
  useEffect(() => {
    if (statusIsLost && fuDueAt) setFuDueAt("");
  }, [statusIsLost, fuDueAt]);

  const submit = async () => {
    if (followUpRequired && !fuDueAt) {
      toast.error("Next follow-up date & time is required for Hot / Warm leads");
      return;
    }
    // Booking a NEW appointment requires a date.
    if (isAppointmentBooked && !hasLiveAppointment && !apptDate) {
      toast.error("Appointment date is required");
      return;
    }
    // Rescheduling an existing appointment requires a NEW date/time.
    if (isRescheduling && (!apptDate || new Date(apptDate).toISOString() === new Date(existingAppt.scheduled_at).toISOString())) {
      toast.error("Pick a new date & time to reschedule");
      return;
    }
    setSaving(true);
    try {
      // Appointment date/time tied to THIS call, so the call card can show
      // APPOINTMENT ON (like FOLLOW-UP ON): a freshly booked/rescheduled date if
      // the caller set one, else the existing appointment being confirmed.
      const apptAtCallIso =
        apptDate ? new Date(apptDate).toISOString()
        : (hasLiveAppointment && existingAppt?.scheduled_at) ? existingAppt.scheduled_at
        : null;
      // A lost status closes the lead — never carry a follow-up on the call or
      // create one below, even if a date lingered in state.
      const fuIso = !statusIsLost && fuDueAt ? new Date(fuDueAt).toISOString() : null;
      const res = await api.post(`/leads/${leadId}/calls`, {
        notes,
        call_type: callType,
        // Snapshot the status + follow-up on the call so the call card can show
        // them per call (STATUS / FOLLOW-UP ON in the LeadPixie-style list).
        status_at_call: newStatus || null,
        follow_up_at_call: fuIso,
        appointment_at_call: apptAtCallIso,
      });
      const callId = res.data.id;

      // The remaining writes are independent of each other (different records /
      // endpoints), so run them concurrently — same as the mobile app — cutting
      // the save from up to 5 sequential round trips down to 2. Only the
      // lead-update→status-note pair must stay ordered, so it's chained inside
      // one entry.
      const writes = [];

      if (recordingFile && callId) {
        const formData = new FormData();
        formData.append("file", recordingFile);
        writes.push(api.post(`/leads/${leadId}/calls/${callId}/recording`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      }

      // Managing an existing appointment (confirm / reschedule / cancel / no-show):
      // update the REAL appointment record rather than creating a duplicate. The
      // backend syncs any date change back to the lead.
      if (hasLiveAppointment && (apptStatus !== existingAppt.status || isRescheduling)) {
        const apptBody = { status: apptStatus };
        if (isRescheduling && apptDate) apptBody.scheduled_at = new Date(apptDate).toISOString();
        writes.push(api.put(`/appointments/${existingAppt.id}`, apptBody));
      }

      // Booking a NEW appointment: create a real appointment record. The backend
      // /appointments endpoint sets the lead's status=appointment_booked,
      // appointment_date, has_appointment + appointment_booked_at — so we must
      // NOT push status/appointment_date via the lead PUT here (that would only
      // update the lead and never create the appointment).
      const bookingAppt = isAppointmentBooked && !hasLiveAppointment && apptDate;
      if (bookingAppt) {
        writes.push(api.post("/appointments", {
          lead_id: leadId,
          patient_name: lead.name,
          mobile: lead.mobile || "",
          branch_id: newBranchId || lead.branch_id || null,
          scheduled_at: new Date(apptDate).toISOString(),
        }));
      }

      const updates = {};
      // The appointment endpoint owns the status when booking. When managing an
      // existing appointment, the caller's chosen status flows through (e.g.
      // cancel/no-show moves the lead out of "booked").
      if (!bookingAppt && newStatus && newStatus !== lead.status) updates.status = newStatus;
      if (newStage && newStage !== lead.stage) updates.stage = newStage;
      if (newLocation !== (lead.location || "")) updates.location = newLocation;
      if (newConcern !== (lead.concern || "")) updates.concern = newConcern;
      if (Object.keys(updates).length > 0) {
        writes.push((async () => {
          await api.put(`/leads/${leadId}`, updates);
          if (updates.status) {
            await api.post(`/leads/${leadId}/notes`, { text: `Status changed to ${statusLabel(updates.status)} (via call log)` });
          }
        })());
      }

      // Schedule the next follow-up if a date was set. The simplified form only
      // asks for the date/time — title/type/priority/channel use backend defaults.
      // fuIso is null for lost statuses, so no follow-up is ever created for them.
      if (fuIso) {
        writes.push(api.post("/followups", {
          lead_id: leadId,
          title: `Follow up with ${lead.name}`,
          due_at: fuIso,
          // Replace any prior pending follow-up so this becomes the next one.
          supersede_pending: true,
        }));
      }

      if (writes.length > 0) await Promise.all(writes);

      toast.success("Call logged");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-cream/95 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl font-semibold text-navy">Submit Call Log</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Fill in the below details</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="text-navy/60 hover:text-navy">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-5">
          {/* Call Type */}
          <Field label="Call Type *">
            <Select value={callType} onValueChange={setCallType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CALL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {/* Stage & Status */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Stage">
              <SearchableSelect
                value={newStage}
                onChange={setNewStage}
                // Disposition offers only the five working stages; a lead already
                // holding another stage keeps it selectable so it doesn't blank.
                options={LEAD_STAGES
                  .filter((s) => isDispositionStage(s.value) || s.value === newStage)
                  .map((s) => ({ value: s.value, label: s.label }))}
                placeholder="Select stage"
                searchPlaceholder="Search stage…"
              />
            </Field>
            <Field label="Status">
              <SearchableSelect
                value={newStatus}
                onChange={setNewStatus}
                options={LEAD_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
                placeholder="Select status"
                searchPlaceholder="Search status…"
              />
            </Field>
          </div>

          {/* Appointment Status — shown when this lead already has a live
              appointment. Confirm / reschedule / cancel the REAL appointment
              record on a follow-up call (replaces the disconnected sub-status). */}
          {hasLiveAppointment && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 space-y-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-800">
                <CalendarClock className="h-3.5 w-3.5" />
                Appointment on {existingAppt.scheduled_at ? formatDateTime(existingAppt.scheduled_at) : "—"}
              </p>
              <Field label="Appointment Status">
                <SearchableSelect
                  value={apptStatus}
                  onChange={setApptStatus}
                  options={MANAGE_APPT_STATUSES.map((s) => ({ value: s, label: APPOINTMENT_STATUS.find((a) => a.value === s)?.label || statusLabel(s) }))}
                  placeholder="Select appointment status"
                  searchPlaceholder="Search…"
                />
              </Field>
              {isRescheduling && (
                <Field label={<span>New Date &amp; Time <span className="text-red-500">*</span></span>}>
                  <Input type="datetime-local" value={apptDate} onChange={(e) => setApptDate(e.target.value)} data-testid="reschedule-date-input" />
                </Field>
              )}
            </div>
          )}

          {/* Location & Concern — Location is the lead's locality/area (from the
              HQ-managed master-data list), distinct from Branch (our centre),
              which now lives in the appointment-booking block below. */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location">
              <SearchableSelect
                value={newLocation}
                onChange={setNewLocation}
                options={LEAD_LOCATIONS.map((l) => ({ value: l, label: l }))}
                placeholder="Select location"
                searchPlaceholder="Search location…"
              />
            </Field>
            <Field label="Concern">
              <SearchableSelect
                value={newConcern}
                onChange={setNewConcern}
                options={LEAD_CONCERNS.map((c) => ({ value: c, label: c }))}
                placeholder="Select concern"
                searchPlaceholder="Search concern…"
              />
            </Field>
          </div>

          {/* Notes */}
          <Field label="Call Notes">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter notes here..." rows={3} />
          </Field>

          {/* Recording */}
          <Field label="Recording (optional)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-navy/20 px-3 py-2.5 text-sm text-muted-foreground hover:bg-cream/40">
              <Mic className="h-4 w-4" />
              {recordingFile ? recordingFile.name : "Attach call recording"}
              <input type="file" className="hidden" accept=".mp3,.m4a,.aac,.wav,.ogg,.webm,.amr,.3gp"
                onChange={(e) => { setRecordingFile(e.target.files[0] || null); }} />
            </label>
            {recordingFile && (
              <button onClick={() => setRecordingFile(null)} className="mt-1 text-xs text-red-500 hover:underline">Remove</button>
            )}
          </Field>

          {/* Appointment booking — only when booking a NEW appointment.
              Managing an existing one uses the Appointment Status block above.
              Branch (our centre) is captured here, alongside the date. */}
          {isAppointmentBooked && !hasLiveAppointment && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Branch">
                <SearchableSelect
                  value={newBranchId}
                  onChange={setNewBranchId}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  placeholder="Select branch"
                  searchPlaceholder="Search branch…"
                />
              </Field>
              <Field label={<span>Appointment Date &amp; Time <span className="text-red-500">*</span></span>}>
                <Input type="datetime-local" value={apptDate} onChange={(e) => setApptDate(e.target.value)} data-testid="appt-date-input" />
              </Field>
            </div>
          )}

          {/* Next Follow-up / Callback — simplified: preset dropdown + date/time.
              Hidden entirely when the chosen status closes the lead. */}
          {!statusIsLost && (
            <div className={`rounded-xl border bg-white p-4 ${followUpRequired ? "border-amber-300" : "border-navy/10"}`}>
              <p className="text-sm font-semibold text-navy">
                Next Followup On / Callback
                {followUpRequired && <span className="text-red-500"> *</span>}
              </p>
              <p className="mb-3 text-xs text-muted-foreground">
                {followUpRequired
                  ? "Required for Hot / Warm leads — set the next follow-up before saving"
                  : isVisited
                  ? "Optional — the lead has visited, no follow-up needed"
                  : "Optional — pick when to follow up"}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <NextFollowupPresetSelect value={fuDueAt} onChange={setFuDueAt} />
                <Input type="datetime-local" value={fuDueAt} onChange={(e) => setFuDueAt(e.target.value)} data-testid="fu-due-input" />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
            <Button onClick={submit} disabled={saving || (followUpRequired && !fuDueAt) || (isAppointmentBooked && !hasLiveAppointment && !apptDate) || (isRescheduling && !apptDate)} className="flex-1 bg-navy text-white hover:bg-navy-hover" data-testid="save-call-btn">
              {saving ? "Saving…" : fuDueAt ? "Save & Schedule Follow-up" : "Save Call Log"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Quick-pick presets for "Next Followup On" — each sets the datetime relative
// to now. "custom" is a no-op placeholder so the user edits the date field.
const FOLLOWUP_TIME_PRESETS = [
  { value: "5m",    label: "In 5 minutes",  ms: 5 * 60000 },
  { value: "10m",   label: "In 10 minutes", ms: 10 * 60000 },
  { value: "30m",   label: "In 30 minutes", ms: 30 * 60000 },
  { value: "1h",    label: "In 1 hour",     ms: 60 * 60000 },
  { value: "3h",    label: "In 3 hours",    ms: 3 * 3600000 },
  { value: "1d",    label: "Tomorrow",      ms: 24 * 3600000 },
  { value: "1w",    label: "In 1 week",     ms: 7 * 24 * 3600000 },
  { value: "1mo",   label: "In 1 month",    ms: 30 * 24 * 3600000 },
  { value: "custom", label: "Custom",       ms: null },
];

// Turns a Date into the value a <input type="datetime-local"> expects, in LOCAL
// time (toISOString would shift by the tz offset).
function toLocalDatetimeValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NextFollowupPresetSelect({ value, onChange }) {
  return (
    <Select
      value=""
      onValueChange={(v) => {
        const p = FOLLOWUP_TIME_PRESETS.find((x) => x.value === v);
        if (!p || p.ms == null) return; // "Custom" → let the user edit the date field
        onChange(toLocalDatetimeValue(new Date(Date.now() + p.ms)));
      }}
    >
      <SelectTrigger data-testid="fu-preset-select"><SelectValue placeholder="Choose when…" /></SelectTrigger>
      <SelectContent>
        {FOLLOWUP_TIME_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function BookAppointmentDialog({ open, onOpenChange, lead, doctors, branches, md, onDone }) {
  const { user } = useAuth();
  const SERVICES = md.services || [];
  const isDoctor = user?.role === "doctor";

  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [date, setDate] = useState(todayISO());
  const [availability, setAvailability] = useState(null);
  const [allAppointments, setAllAppointments] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ service: "", duration_minutes: "15" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Same branch rules as the Book Appointment page; the lead's own branch is
  // preferred when the user may book there. Its hours drive "Any doctor".
  const [branchId, setBranchId] = useState("");
  const branchOptions = React.useMemo(() => bookableBranches(user, branches), [user, branches]);
  const branchObj = defaultBranch(branchOptions, user, branchId, lead?.branch_id, localStorage.getItem(BOOK_BRANCH_KEY));
  const pickBranch = (v) => { setBranchId(v); localStorage.setItem(BOOK_BRANCH_KEY, v); setSelectedSlot(null); };

  useEffect(() => {
    if (open) {
      setForm({ service: lead?.interested_service || "", duration_minutes: "15" });
      setBranchId("");
      setSelectedSlot(null);
      setDate(todayISO());
      if (isDoctor) {
        setSelectedDoctor(user?.id || "");
      } else {
        setSelectedDoctor("");
      }
      api.get("/appointments").then((r) => setAllAppointments(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!selectedDoctor) { setAvailability(null); return; }
    api.get(`/users/${selectedDoctor}/availability`).then((r) => setAvailability(r.data)).catch(() => setAvailability(null));
  }, [selectedDoctor]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    setSelectedSlot(null);
  };

  const docObj = doctors.find((d) => d.id === selectedDoctor);
  const niceDate = new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  // Any doctor → the branch's hours/closed days; a doctor → their availability.
  const { slots, dayInfo, closedNote } = React.useMemo(
    () => computeSlots({ doctorId: selectedDoctor, availability, branch: branchObj, date, appointments: allAppointments }),
    [selectedDoctor, availability, date, allAppointments, branchObj]);

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    const d = new Date(date + "T00:00:00");
    d.setHours(Math.floor(slot.minutes / 60), slot.minutes % 60, 0, 0);
    setSelectedSlot({ time: slot.label, iso: d.toISOString() });
  };

  const submit = async () => {
    if (!selectedSlot) { toast.error("Pick an available time slot"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", {
        lead_id: lead.id,
        patient_name: lead.name,
        mobile: lead.mobile || "",
        service: form.service,
        doctor_id: selectedDoctor || null,
        branch_id: branchObj?.id || lead.branch_id || null,
        scheduled_at: selectedSlot.iso,
        duration_minutes: parseInt(form.duration_minutes, 10) || 15,
      });
      toast.success(docObj
        ? `Booked ${lead.name} with ${docObj.name} at ${selectedSlot.time}`
        : `Booked ${lead.name} at ${selectedSlot.time} — doctor assigned at check-in`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex flex-col max-h-[90vh]">
        <DialogHeader><DialogTitle className="font-display text-navy">Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2 overflow-y-auto flex-1 -mx-6 px-6">
          {/* Lead header */}
          <div className="rounded-lg bg-cream/60 px-3 py-2">
            <p className="font-semibold text-ink">{lead.name}</p>
            {lead.mobile && <p className="text-xs text-muted-foreground">{maskPhone(lead.mobile)}</p>}
          </div>

          {/* Doctor + Branch + Date controls */}
          <div className="flex flex-wrap items-end gap-3">
            {!isDoctor && (
              <Field label="Doctor">
                <Select value={selectedDoctor || ANY_DOCTOR} onValueChange={(v) => { setSelectedDoctor(v === ANY_DOCTOR ? "" : v); setSelectedSlot(null); }}>
                  <SelectTrigger className="h-9 w-48 bg-white" data-testid="lead-appt-doctor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_DOCTOR}>Any doctor — assign later</SelectItem>
                    {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {branchOptions.length > 0 && (
              <Field label="Branch">
                <Select value={branchObj?.id || ""} onValueChange={pickBranch}>
                  <SelectTrigger className="h-9 w-44 bg-white" data-testid="lead-appt-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{branchOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <div className="flex items-end gap-1.5">
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" className="h-9 text-xs" onClick={() => { setDate(todayISO()); setSelectedSlot(null); }}>Today</Button>
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(1)}><ChevronRight className="h-4 w-4" /></Button>
              <Input type="date" value={date} onChange={(e) => { setDate(e.target.value || todayISO()); setSelectedSlot(null); }} className="h-9 w-36 bg-white" />
            </div>
          </div>

          {/* Slot grid */}
          {!dayInfo ? (
            <div className="rounded-lg border border-navy/10 bg-white p-8 text-center text-sm text-muted-foreground">
              {selectedDoctor
                ? `${docObj?.name || "Doctor"} is not available on ${niceDate}.`
                : (closedNote || "The clinic is closed on this day.")}
            </div>
          ) : (
            <div className="rounded-lg border border-navy/10 bg-white p-4">
              {!selectedDoctor && dayInfo.fallback && (
                <p className="mb-2 text-xs text-amber-700" data-testid="lead-appt-branch-hours-fallback">Branch hours not set — using 9 AM–6 PM</p>
              )}
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-navy">{docObj?.name || "Any doctor"}{branchObj ? ` · ${branchObj.name}` : ""} · {niceDate}</p>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-green-100 border border-green-300" /> Open</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-red-50 border border-red-200" /> Booked</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded bg-gray-100 border border-gray-200" /> Break/Past</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8">
                {slots.map((slot) => {
                  const isSelected = selectedSlot && slot.minutes === (new Date(selectedSlot.iso).getHours() * 60 + new Date(selectedSlot.iso).getMinutes());
                  return (
                    <button
                      key={slot.minutes}
                      onClick={() => handleSlotClick(slot)}
                      disabled={!slot.available}
                      className={`rounded-md border px-1.5 py-2 text-center text-xs font-medium transition-all
                        ${isSelected
                          ? "border-navy bg-navy text-white ring-2 ring-navy/30"
                          : slot.available
                            ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100 hover:border-green-400 cursor-pointer"
                            : slot.isBooked
                              ? "border-red-200 bg-red-50 text-red-400 cursor-not-allowed"
                              : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                        }`}
                      data-testid={`lead-slot-${slot.minutes}`}
                    >
                      {slot.label}
                      {slot.isBooked && <p className="text-[8px] mt-0.5">Booked</p>}
                      {slot.isBreak && <p className="text-[8px] mt-0.5">Break</p>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected slot confirmation + extra fields */}
          {selectedSlot && (
            <div className="space-y-3 rounded-lg border border-green-200 bg-green-50/50 p-3">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-green-700" />
                <div>
                  <p className="text-sm font-semibold text-green-800">{selectedSlot.time} · {niceDate}</p>
                  <p className="text-xs text-green-600">{docObj?.name || "Doctor assigned at check-in"}{branchObj ? ` · ${branchObj.name}` : ""}</p>
                </div>
                <Check className="ml-auto h-5 w-5 text-green-600" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Service">
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Duration">
                  <Select value={form.duration_minutes} onValueChange={(v) => set("duration_minutes", v)}>
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{["15", "30", "45", "60", "90"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 border-t border-navy/10 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!selectedSlot || saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-lead-appointment-btn">
            {saving ? "Booking..." : "Confirm Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
